/**
 * Advanced: one folded row holding everything a working environment almost
 * never needs. Capture limits, test-state recipes, per-service variables and
 * protected preview credentials.
 *
 * It is one row on purpose. Rows of things nobody opens read as unfinished
 * settings; one row reads as the drawer it is. What a run does need to name
 * often lives elsewhere: the tests and the managed services under
 * Application, the origins under Network.
 */
import type { QaEnvironmentConfig, WorkerConfigField } from "@yagni/shared";
import { Button, Input } from "../ui/index.js";
import { EnvironmentPreviewAccess } from "./EnvironmentPreviewAccess.js";
import { ConfigRow, LinesControl, Row, VariablesControl } from "./environmentControls.js";
import { EvidenceFields, type RepositoryChoice } from "./EnvironmentConfigRows.js";

const unresolvedSha = "0".repeat(40);

const WIDTH_FIELD: WorkerConfigField = {
  key: "qa-viewport-width",
  label: "Viewport width",
  help: "The browser size every capture is taken at.",
  type: "number",
  min: 320,
  max: 1920,
};
const HEIGHT_FIELD: WorkerConfigField = {
  key: "qa-viewport-height",
  label: "Viewport height",
  help: "The browser size every capture is taken at.",
  type: "number",
  min: 320,
  max: 1080,
};
const PIN_OPTIONS = [
  { value: "fixed", label: "Fixed commit" },
  { value: "branch", label: "Track branch" },
] as const;

export function advancedSummary(config: QaEnvironmentConfig): string {
  const parts = [
    (config.fixtures ?? []).length ? `${config.fixtures!.length} test states` : null,
    (config.previewAccess ?? []).length ? "protected previews" : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "Capture limits, test states, service variables";
}

/** The Other repositories row's summary: who is checked out beside this one. */
export function otherRepositoriesSummary(
  config: QaEnvironmentConfig,
  repositories: RepositoryChoice[],
): string {
  const dependencies = config.dependencies ?? [];
  if (!dependencies.length) return "None. Add a docs or shared-code repository agents should read.";
  return `${dependencies
    .map((dependency) => {
      const found = repositories.find((repo) => repo.id === dependency.repoId);
      return found ? `${found.owner}/${found.name}` : "Repository unavailable";
    })
    .join(", ")} · checked out beside this one`;
}

/**
 * Other repositories: up to twenty checked out beside this one in every build
 * and environment run, so agents can read a docs repository or shared code.
 */
export function OtherRepositories({
  config,
  repositories,
  primaryRepoId,
  onChange,
  onRefreshDependency,
  onStoreCredential,
}: {
  config: QaEnvironmentConfig;
  repositories: RepositoryChoice[];
  primaryRepoId: string;
  onChange: (patch: Partial<QaEnvironmentConfig>) => void;
  onRefreshDependency: (repoId: string) => void;
  /** Moves a plaintext variable into Variables. */
  onStoreCredential?: (name: string, value: string) => void;
}) {
  const dependencies = config.dependencies ?? [];
  const name = (id: string) => {
    const found = repositories.find((repo) => repo.id === id);
    return found ? `${found.owner}/${found.name}` : "Repository unavailable";
  };
  const available = repositories.filter(
    (repo) =>
      repo.id !== primaryRepoId &&
      !dependencies.some((dependency) => dependency.repoId === repo.id),
  );
  const editDependency = (
    repoId: string,
    patch: Partial<NonNullable<QaEnvironmentConfig["dependencies"]>[number]>,
  ) =>
    onChange({
      dependencies: dependencies.map((dependency) =>
        dependency.repoId === repoId ? { ...dependency, ...patch } : dependency,
      ),
    });

  return (
    <>
      <Row
        title="Add a repository"
        help="Up to twenty repositories are checked out beside this one. Agents read them: a docs repository, shared code, a service this one calls."
      >
        <span className="wrk-srow-controls">
          <select
            className="wrk-control"
            aria-label="Add a repository"
            value=""
            disabled={dependencies.length >= 20 || available.length === 0}
            onChange={(event) => {
              const repoId = event.target.value;
              if (dependencies.length >= 20 || !available.some((repo) => repo.id === repoId))
                return;
              onChange({
                dependencies: [
                  ...dependencies,
                  {
                    repoId,
                    ref: "main",
                    sha: unresolvedSha,
                    pin: "fixed",
                    setupCommands: [],
                  },
                ],
              });
            }}
          >
            <option value="">Choose a repository</option>
            {available.map((repo) => (
              <option key={repo.id} value={repo.id}>
                {repo.owner}/{repo.name}
              </option>
            ))}
          </select>
        </span>
      </Row>
      {dependencies.map((dependency) => (
        <Row
          key={dependency.repoId}
          title={name(dependency.repoId)}
          help={
            dependency.sha === unresolvedSha
              ? "The commit resolves when this is saved."
              : `Pinned commit ${dependency.sha.slice(0, 12)}`
          }
          testId={`qa-dependency-${dependency.repoId}`}
        >
          <span className="wrk-srow-controls qa-service">
            <label>
              Branch or tag for {name(dependency.repoId)}
              <Input
                value={dependency.ref}
                onChange={(event) => editDependency(dependency.repoId, { ref: event.target.value })}
              />
            </label>
            <label>
              How {name(dependency.repoId)} is pinned
              <select
                value={dependency.pin ?? "fixed"}
                onChange={(event) =>
                  editDependency(dependency.repoId, {
                    pin: event.target.value as NonNullable<
                      QaEnvironmentConfig["dependencies"]
                    >[number]["pin"],
                  })
                }
              >
                {PIN_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Setup commands for {name(dependency.repoId)}
              <LinesControl
                label={`Setup commands for ${name(dependency.repoId)}`}
                value={dependency.setupCommands}
                onCommit={(setupCommands) => editDependency(dependency.repoId, { setupCommands })}
              />
            </label>
            <label>
              Variables for {name(dependency.repoId)}
              <VariablesControl
                label={`Variables for ${name(dependency.repoId)}`}
                value={dependency.environmentDefaults}
                onCommit={(environmentDefaults) =>
                  editDependency(dependency.repoId, { environmentDefaults })
                }
                onStoreCredential={
                  onStoreCredential &&
                  ((variable, value) => {
                    const { [variable]: _moved, ...rest } = dependency.environmentDefaults ?? {};
                    editDependency(dependency.repoId, {
                      environmentDefaults: Object.keys(rest).length ? rest : undefined,
                    });
                    onStoreCredential(variable, value);
                  })
                }
              />
            </label>
            {(dependency.pin ?? "fixed") === "fixed" && (
              <Button
                variant="secondary"
                disabled={dependency.sha === unresolvedSha}
                aria-label={`Refresh commit for ${name(dependency.repoId)}`}
                onClick={() => onRefreshDependency(dependency.repoId)}
              >
                Refresh commit
              </Button>
            )}
            <Button
              variant="ghost"
              aria-label={`Remove ${name(dependency.repoId)}`}
              onClick={() =>
                onChange({
                  dependencies: dependencies.filter((item) => item.repoId !== dependency.repoId),
                  services: config.services.map((service) => {
                    if (service.repoId !== dependency.repoId) return service;
                    const { repoId: _repoId, ...primaryService } = service;
                    return primaryService;
                  }),
                })
              }
            >
              Remove
            </Button>
          </span>
        </Row>
      ))}
      {dependencies.length > 0 && (
        <Row title="Removing a dependency">
          <span className="wrk-srow-controls qa-note">
            Its services move back to the primary repository. Review those commands before saving.
          </span>
        </Row>
      )}
    </>
  );
}

export function EnvironmentAdvanced({
  config,
  credentialNames,
  onChange,
  onOpenRow,
  onStoreCredential,
}: {
  config: QaEnvironmentConfig;
  credentialNames: string[];
  onChange: (patch: Partial<QaEnvironmentConfig>) => void;
  /** Opens another settings row by its section key. */
  onOpenRow?: (section: string) => void;
  /** Moves a plaintext variable into Variables. */
  onStoreCredential?: (name: string, value: string) => void;
}) {
  const editService = (index: number, patch: Partial<QaEnvironmentConfig["services"][number]>) =>
    onChange({
      services: config.services.map((service, n) =>
        n === index ? { ...service, ...patch } : service,
      ),
    });

  return (
    <>
      <h3 className="qa-subhead">Capture</h3>
      <EvidenceFields config={config} onChange={onChange} />
      <ConfigRow
        field={WIDTH_FIELD}
        value={config.viewport.width}
        onChange={(value) =>
          onChange({
            viewport: { ...config.viewport, width: Number(value) },
          })
        }
      />
      <ConfigRow
        field={HEIGHT_FIELD}
        value={config.viewport.height}
        onChange={(value) =>
          onChange({
            viewport: { ...config.viewport, height: Number(value) },
          })
        }
      />
      <Row
        title="Sensitive element selectors"
        help="Masked in every capture. Masks are best-effort."
      >
        <span className="wrk-srow-controls">
          <LinesControl
            label="Sensitive element selectors"
            rows={2}
            value={config.maskSelectors}
            onCommit={(maskSelectors) => onChange({ maskSelectors })}
          />
        </span>
      </Row>

      <h3 className="qa-subhead">Test states</h3>
      <Row
        title="Prepared states"
        help="Approved ways to create empty, populated, or error states using synthetic data, inside the sandbox."
      >
        <span className="wrk-srow-controls">
          <Button
            variant="secondary"
            disabled={(config.fixtures?.length ?? 0) >= 10}
            onClick={() =>
              onChange({
                fixtures: [...(config.fixtures ?? []), { name: "", description: "", command: "" }],
              })
            }
          >
            Add test-state recipe
          </Button>
        </span>
      </Row>
      {(config.fixtures ?? []).map((fixture, index) => (
        <Row
          key={index}
          title={fixture.name || `Recipe ${index + 1}`}
          testId={`qa-fixture-${index}`}
        >
          <span className="wrk-srow-controls qa-service">
            <label>
              Recipe name
              <Input
                value={fixture.name}
                onChange={(event) =>
                  onChange({
                    fixtures: config.fixtures!.map((item, n) =>
                      n === index ? { ...item, name: event.target.value } : item,
                    ),
                  })
                }
              />
            </label>
            <label>
              What it prepares
              <Input
                value={fixture.description}
                onChange={(event) =>
                  onChange({
                    fixtures: config.fixtures!.map((item, n) =>
                      n === index ? { ...item, description: event.target.value } : item,
                    ),
                  })
                }
              />
            </label>
            <label>
              Preparation command
              <Input
                value={fixture.command}
                onChange={(event) =>
                  onChange({
                    fixtures: config.fixtures!.map((item, n) =>
                      n === index ? { ...item, command: event.target.value } : item,
                    ),
                  })
                }
              />
            </label>
            <Button
              variant="ghost"
              onClick={() =>
                onChange({
                  fixtures: config.fixtures!.filter((_, n) => n !== index),
                })
              }
            >
              Remove recipe
            </Button>
          </span>
        </Row>
      ))}

      <h3 className="qa-subhead">Service variables</h3>
      {config.services.length === 0 && (
        <Row title="No services yet">
          <span className="wrk-srow-controls qa-note">
            Add a service under Application to give it its own variables.
          </span>
        </Row>
      )}
      {config.services.map((service, index) => (
        <Row
          key={`${service.name}-${index}`}
          title={`Variables for ${service.name || `service ${index + 1}`}`}
          help="Applied to this service only, over the repository and environment defaults. Values here are plain text; secrets belong in Variables, where values are write-only."
        >
          <span className="wrk-srow-controls">
            <VariablesControl
              label={`Variables for ${service.name || `service ${index + 1}`}`}
              value={service.env}
              onCommit={(env) => editService(index, { env })}
              onStoreCredential={
                onStoreCredential &&
                ((variable, value) => {
                  const { [variable]: _moved, ...rest } = service.env ?? {};
                  editService(index, {
                    env: Object.keys(rest).length ? rest : undefined,
                  });
                  onStoreCredential(variable, value);
                })
              }
            />
          </span>
        </Row>
      ))}

      <h3 className="qa-subhead">Protected preview access</h3>
      <EnvironmentPreviewAccess
        config={config}
        credentialNames={credentialNames}
        onChange={onChange}
        onOpenRow={onOpenRow}
      />
    </>
  );
}
